import React, {useState, useEffect} from 'react';
import {StyleSheet} from 'react-native';
import copyForPast from '../../../../../utils/ui/copy-paste';
import {Box, Center, Divider, Heading} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import InternalTemplate from '../../../../../components/templates/internal';
import {StackParamList} from '../../../../../navigators';
import {SpinnerModal} from '../../../../../components/modals';
import {message as t} from '../../../../../utils/messages';
import {SimpleDropDown} from '../../../../../components/inputs';
import IDropDownOption from '../../../../../interfaces/ui/dropdown-option.interface';
import InputTextCopy from '../../../../../components/inputs/copy-text';
import useToast from '../../../../../hooks';
import {useContextState} from '../../../../../context/user';
import {useContextState as userSubClientContext} from '../../../../../context/sub-client';
import {IBank} from '../../../../../context/user/Types';
import {IDocumentDto} from '../../../../../interfaces/dtos';
import Option from './options';
import {
  IDocumentItemsBolivar,
  IDocumentItemsDollar,
} from '../../../../../interfaces/dtos/document.interface';
import {isWebApp} from '../../../../../utils/platform/is-web';

type ScreenProps = NativeStackScreenProps<StackParamList, 'Calculator'>;

const LOCAL_CURRENCY = 'VES';

interface ICheckedOption {
  plates: boolean;
  iva: boolean;
  igtf: boolean;
}

const DEFAULT_CHECKED_OPTIONS = {
  plates: false,
  igtf: false,
  iva: false,
} as ICheckedOption;

const PaymentCalculator = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [documentList, setDocumentList] = useState([] as IDropDownOption[]);
  const {showCopiedSignal} = useToast();
  const [paymentMethodList, setPaymentMethodList] = useState(
    [] as IDropDownOption[],
  );
  const {banks} = useContextState();
  const {fetchDocumentList, documents} = userSubClientContext();
  const [paymentMethod, setPaymentMethod] = useState(null as IBank | null);
  const [document, setDocument] = useState(null as IDocumentDto | null);
  const [checkedOptions, setCheckedOptions] = useState(DEFAULT_CHECKED_OPTIONS);

  useEffect(() => {
    if (isLoading) {
      getDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (documents) {
      setDocumentList(
        documents?.map((item: IDocumentDto, position: number) => {
          const {label} = item;
          return {value: position.toString(), label};
        }) as IDropDownOption[],
      );
    }
  }, [documents]);

  const getBanksOptions = () => {
    const list = [] as IDropDownOption[];
    if (document) {
      for (let index = 0; index < banks.length; index++) {
        const bank = banks[index];

        if (
          (document.currency === LOCAL_CURRENCY && bank.source?.foreign) ||
          (document.currency !== LOCAL_CURRENCY && !bank.source?.foreign)
        ) {
          continue;
        }

        list.push({
          value: bank.value,
          label: bank.title,
        } as IDropDownOption);
      }
    }
    return list;
  };

  useEffect(() => {
    if (documentList && banks) {
      setPaymentMethodList([...getBanksOptions()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, banks, documentList]);

  const getDocuments = async () => {
    await fetchDocumentList();
    setIsLoading(false);
  };

  const formatMoney = (value: number | undefined) => {
    const emptyValue = 0;
    if (value) {
      return value.toLocaleString('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return emptyValue.toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleGoBack = () => {
    navigation.navigate('Dashboard');
  };

  const handleOnChangeDocument = (value: string) => {
    if (documents) {
      const found = documents[Number(value)];
      if (found) {
        setPaymentMethodList([]);
        setDocument(found);
        setPaymentMethod(null);
        setCheckedOptions(DEFAULT_CHECKED_OPTIONS);
      }
    }
  };

  const handleOnChangePaymentMethod = (value: string) => {
    const found = banks.find(p => p.value === value);
    if (found && found.source) {
      setPaymentMethod(found.source);
    }
  };

  const handleOnCopy = () => {
    if (paymentMethod) {
      copyForPast(getCalculationValue());
    } else {
      copyForPast(' ');
    }
    showCopiedSignal();
  };

  const getCalculationValue = () => {
    if (paymentMethod) {
      let calculationValue = getAmountValue();
      if (checkedOptions.iva) {
        calculationValue += getIvaValue();
      }
      if (checkedOptions.plates) {
        calculationValue += getPlatesValue();
      }
      if (checkedOptions.igtf) {
        calculationValue += getIgtfValue();
      }
      return formatMoney(calculationValue + getComissionValue());
    }
    return '';
  };

  const formatCalculationValue = () => {
    if (paymentMethod) {
      return `${
        paymentMethod?.foreign ? '$' : 'Bs.'
      }  ${getCalculationValue()}`;
    }
    return '';
  };

  const getAmountValue = (): number => {
    if (paymentMethod?.foreign) {
      const paymentMethodForeign = document?.items as IDocumentItemsDollar;
      return paymentMethodForeign.amount.value;
    }
    const paymentMethodLocal = document?.items as IDocumentItemsBolivar;
    return paymentMethodLocal.amount.value;
  };

  const getComissionValue = (): number => {
    if (paymentMethod && paymentMethod.comission) {
      let amount = getAmountValue();
      if (checkedOptions.iva) {
        amount += getIvaValue();
      }
      if (checkedOptions.plates) {
        amount += getPlatesValue();
      }
      if (checkedOptions.igtf) {
        amount += getIgtfValue();
      }
      return (
        paymentMethod.comission?.fixed +
        amount * paymentMethod.comission?.percent
      );
    }
    return 0;
  };

  const getIvaValue = (): number => {
    if (paymentMethod?.foreign) {
      const paymentMethodForeign = document?.items as IDocumentItemsDollar;
      return paymentMethodForeign.iva.value;
    }
    const paymentMethodLocal = document?.items as IDocumentItemsBolivar;
    return paymentMethodLocal.iva.value;
  };

  const getIgtfValue = (): number => {
    if (paymentMethod?.foreign) {
      const paymentMethodForeign = document?.items as IDocumentItemsDollar;
      let igtfTotal = paymentMethodForeign.amount.igtf;
      if (checkedOptions.iva) {
        igtfTotal = igtfTotal + paymentMethodForeign.iva.igtf;
      }
      if (checkedOptions.plates) {
        igtfTotal = igtfTotal + paymentMethodForeign.plates.igtf;
      }
      return igtfTotal;
    }
    const paymentMethodLocal = document?.items as IDocumentItemsBolivar;
    return paymentMethodLocal.igtf.value;
  };

  const getPlatesValue = (): number => {
    if (paymentMethod?.foreign) {
      const paymentMethodForeign = document?.items as IDocumentItemsDollar;
      return paymentMethodForeign.plates.value;
    }
    const paymentMethodLocal = document?.items as IDocumentItemsBolivar;
    return paymentMethodLocal.plates.value;
  };

  const Options = () => {
    const handleIVA = (value: boolean) => {
      setCheckedOptions({...checkedOptions, iva: value});
    };
    const handlePlates = (value: boolean) => {
      setCheckedOptions({...checkedOptions, plates: value});
    };
    const handleIGTF = (value: boolean) => {
      setCheckedOptions({...checkedOptions, igtf: value});
    };

    const hasCommision = () => {
      return true;
    };

    return (
      <Box marginTop={5}>
        {getAmountValue() > 0 && (
          <Option
            hideCheck={false}
            key={'optionAmount'}
            label={t('calculator-option-amount')}
            value={formatMoney(getAmountValue())}
          />
        )}

        <Option
          key={'optionIVA'}
          label={t('calculator-option-iva')}
          onChange={value => handleIVA(value)}
          value={formatMoney(getIvaValue())}
          checked={checkedOptions.iva}
        />
        <Option
          key={'optionPlates'}
          label={t('calculator-option-plates')}
          onChange={value => handlePlates(value)}
          value={formatMoney(getPlatesValue())}
          checked={checkedOptions.plates}
        />
        <Option
          key={'optionIGTF'}
          label={t('calculator-option-igtf')}
          onChange={value => handleIGTF(value)}
          value={formatMoney(getIgtfValue())}
          checked={checkedOptions.igtf}
        />
        {hasCommision() && (
          <Option
            hideCheck={false}
            key={'optionComission'}
            label={t('calculator-option-comission')}
            value={formatMoney(getComissionValue())}
            help="Monto estimado, puede variar"
          />
        )}
      </Box>
    );
  };

  const LabelOptions = () => {
    return (
      <>
        <Heading fontSize={14} color={'#858597'} fontWeight={'normal'}>
          {paymentMethod?.foreign
            ? t('Detalle Dólares')
            : t('Detalle Bolívares')}
        </Heading>
      </>
    );
  };

  return (
    <InternalTemplate
      title={'screen-payment-calculator-title'}
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} />
      <Box flex={1}>
        <KeyboardAwareScrollView
          style={styles.content}
          enableOnAndroid={true}
          scrollEnabled={true}
          enableAutomaticScroll={true}
          showsVerticalScrollIndicator={isWebApp}>
          <Center width="100%" flex={1} justifyContent="flex-start">
            <Box width="100%" py={10}>
              <Heading
                fontSize="16"
                paddingBottom="3"
                color="#06355E"
                width="100%">
                {t('calculator-subtitle-payment')}
              </Heading>
              <SimpleDropDown
                label="calculator-label-input-documents"
                options={documentList ?? []}
                onChange={handleOnChangeDocument}
              />
              <SimpleDropDown
                label="calculator-label-input-payment-method"
                options={paymentMethodList}
                onChange={handleOnChangePaymentMethod}
              />
              {paymentMethod && (
                <>
                  <LabelOptions />
                  <Options />
                </>
              )}
              <Divider bg="#C8E5FF" orientation="horizontal" mb={3} mt={1} />
              <InputTextCopy
                label={'calculator-label-input-amount'}
                erroMessage={''}
                backgroundColor="#DBDBDB"
                value={formatCalculationValue()}
                selectedColor="#C8E5FF"
                isReadOnly={true}
                onCopy={handleOnCopy}
              />
            </Box>
          </Center>
        </KeyboardAwareScrollView>
      </Box>
    </InternalTemplate>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
});

export default PaymentCalculator;
